'use client';

import React from 'react';
import { IconLike, IconDislike } from '@repo/svgs';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { useOwner } from '../../context/OwnerProvider';
import dynamic from 'next/dynamic';
import type { ViolationRecord } from '@repo/api';
import { TextBadge, ViolationCell, BuildingMetric, Divider } from '@/components/ui';
import { ModalTrigger } from '@/components/ui/ModalTrigger';
import { AboutNycOpenViolationsTrigger } from '@/components/shared/violations/AboutNycOpenViolationsTrigger';
import { NycOpenViolationsModal } from '@/components/shared/violations/NycOpenViolationsModal';

// NYC open violations modal is shared across building + contact.

const toSeverity = (klass: 'Class I' | 'Class A' | 'Class B' | 'Class C') => {
  switch (klass) {
    case 'Class C':
      return 'highest';
    case 'Class B':
      return 'high';
    case 'Class A':
      return 'medium';
    case 'Class I':
    default:
      return 'low';
  }
};

const toDescription = (klass: 'Class I' | 'Class A' | 'Class B' | 'Class C') => {
  switch (klass) {
    case 'Class C':
      return 'Rodents, pest, mold, inadequate heat or hot water, defective building parts';
    case 'Class B':
      return 'Smoke detector issues, inadequate lighting, no lighting for stairways';
    case 'Class A':
      return 'No peephole on a door, no street number on the building, unlawful keeping of animals';
    case 'Class I':
      return 'Missing or non-compliant with administrative information orders or filings';
  }
};

export function OwnerOpenViolations() {
  const { owner } = useOwner();
  const openViolations = owner?.openData?.openViolations;

  if (!openViolations) {
    return null;
  }

  const { summary, data: violations } = openViolations;

  // Ensure violations exists with default empty object
  const safeViolations = violations || {};

  const numUnits = owner?.numUnits ?? null;
  const hasUnits = !!numUnits && numUnits > 0;

  const perUnitContact = hasUnits ? (summary.perUnitContact ?? summary.total / numUnits) : null;
  const perUnitCity = summary.perUnitCity ?? null;
  const canCompare = hasUnits && perUnitContact !== null && perUnitCity !== null;
  const isBetter = canCompare ? perUnitContact < perUnitCity : false;

  const formatOwnerAverage = (value: number | null) => {
    if (!hasUnits) return '--';
    if (value === null || Number.isNaN(value)) return '0';
    const prefix = value === 0 ? 'Less than ' : '';
    const displayValue = value > 0 ? value.toFixed(2) : '0.01';
    return `${prefix}${displayValue}`;
  };

  const formatCityAverage = (value: number | null) => {
    if (value === null || Number.isNaN(value)) return '—';
    const prefix = value ? '' : 'Less than ';
    const displayValue = value > 0 ? value.toFixed(2) : '0.01';
    return `${prefix}${displayValue}`;
  };

  const renderCell = (
    label: 'Class C' | 'Class B' | 'Class A' | 'Class I',
    records: ViolationRecord[],
  ) => {
    const count = records.length;
    const cell = (
      <ViolationCell
        count={count}
        title={label}
        description={toDescription(label)}
        severity={toSeverity(label)}
        trailingIcon={count > 0}
      />
    );

    if (!count) return cell;

    return (
      <ModalTrigger
        label={`Open Violations - ${label}`}
        content={<NycOpenViolationsModal violations={records} violationClass={label} />}
        urlKey={`violations-${label.toLowerCase().replace(' ', '-')}`}
      >
        {cell}
      </ModalTrigger>
    );
  };

  return (
    <div>
      <SectionHeader
        title="Open violations"
        trailingIcon={<AboutNycOpenViolationsTrigger />}
        subtitleText="From past 10 years · Updated 5 days ago"
      />
      <div className="flex flex-col gap-4 md:px-2 pt-2">
        {canCompare ? (
          <div className="px-2">
            <TextBadge
              icon={
                isBetter ? (
                  <IconLike className="w-4 h-4 icon-success" />
                ) : (
                  <IconDislike className="w-4 h-4 icon-alert" />
                )
              }
              variant={isBetter ? 'positive' : 'negative'}
            >
              {isBetter
                ? 'Average violations per unit for this owner or associate is better than the city average.'
                : 'Average violations per unit for this owner or associate is worse than the city average.'}
            </TextBadge>
          </div>
        ) : null}

        <div className="grid gap-4 grid-cols-2 px-2">
          <BuildingMetric label="Owner average" value={formatOwnerAverage(perUnitContact)} />
          <div className="flex items-center gap-4">
            <Divider variant="tertiary" orientation="vertical" className="h-full my-2" />
            <BuildingMetric label="NYC average" value={formatCityAverage(perUnitCity)} />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          {renderCell('Class C', safeViolations.classC || [])}
          <Divider variant="tertiary" />
          {renderCell('Class B', safeViolations.classB || [])}
          <Divider variant="tertiary" />
          {renderCell('Class A', safeViolations.classA || [])}
          <Divider variant="tertiary" />
          {renderCell('Class I', safeViolations.classI || [])}
        </div>
      </div>
    </div>
  );
}
